import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { initGA, logPageView } from '@/utils/analytics';
import CookieConsent from '@/components/CookieConsent';
import PageTracker from '@/components/PageTracker';
import '@/App.css';

// Lazy load pages for better initial load performance
const Home = lazy(() => import('@/pages/Home'));
const AdminLogin = lazy(() => import('@/pages/AdminLogin'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const PageView = lazy(() => import('@/pages/PageView'));
const NewsDetail = lazy(() => import('@/pages/NewsDetail'));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600 border-b-4 mx-auto mb-3"></div>
      <p className="text-slate-600 text-sm">Lädt...</p>
    </div>
  </div>
);

// Redirect component for old page URLs
function PageRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/${slug}`} replace />;
}

// Google Analytics Tracking ID - Replace with your actual ID
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-8TZ67VPVMG';

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    // Initialize GA on app load
    initGA(GA_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    // Log page view on route change
    logPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AnalyticsTracker />
        <PageTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/news/:newsId" element={<NewsDetail />} />
            {/* Old URL formats - redirect to new clean format */}
            <Route path="/page/:slug" element={<PageRedirect />} />
            <Route path="/pages/:slug" element={<PageRedirect />} />
            {/* New clean URL format - must be last to avoid conflicts */}
            <Route path="/:slug" element={<PageView />} />
          </Routes>
        </Suspense>
        {/* GDPR Cookie Consent Banner */}
        <CookieConsent />
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;